import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState, type FormEvent } from "react";
import { Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/login")({ component: LoginPage });

function LoginPage() {
  const { login, isLoading } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await login({ username: "admin", password });
      await navigate({ to: "/" });
    } catch {
      setError("Contraseña incorrecta");
    }
  }

  return (
    <div className="min-h-screen bg-[#F5F3ED] flex items-center justify-center p-4 sm:p-6 lg:p-10">
      <div className="w-full max-w-[1180px] min-h-[680px] overflow-hidden rounded-[28px] border border-[#DEDCD5] bg-white shadow-[0_24px_70px_rgba(9,9,9,0.10)] grid lg:grid-cols-2">
        <div className="hidden lg:block relative bg-[#F5F3ED] border-r border-[#E7E5DF] overflow-hidden">
          <img src="/login-brand.png" alt="Cold Crew — Coffee Concentrate" className="absolute inset-0 size-full object-cover" />
        </div>

        <div className="flex flex-col justify-center px-7 py-12 sm:px-12 lg:px-16 xl:px-20">
          <div className="lg:hidden mb-10 flex justify-center">
            <img src="/logo_sin_fondo.png" alt="Cold Crew" className="h-28 w-auto object-contain" />
          </div>

          <div className="w-full max-w-[480px] mx-auto">
            <div className="mb-10 text-center lg:text-left">
              <p className="text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#FF6500] mb-3">Coffee Concentrate</p>
              <h1 className="font-display text-3xl sm:text-[34px] font-extrabold tracking-[-0.035em] text-[#090909]">COLD CREW MANAGER</h1>
              <p className="mt-2 text-sm sm:text-base text-[#66645F]">Control de pedidos, ventas y gastos</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.08em] text-[#575550]">Contraseña</label>
                <div className="relative">
                  <input
                    id="password" type={showPassword ? "text" : "password"} autoComplete="current-password" required
                    value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••"
                    className="h-14 w-full rounded-xl border border-[#DEDCD5] bg-white px-4 pr-12 text-[15px] text-[#090909] placeholder:text-[#AAA7A0] outline-none hover:border-[#AAA7A0] focus:border-[#FF6500] focus:ring-4 focus:ring-[#FF6500]/10 disabled:opacity-50"
                  />
                  <button type="button" onClick={() => setShowPassword(v => !v)} aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-2 text-[#66645F] hover:bg-[#FFF0E5] hover:text-[#FF6500]">
                    {showPassword ? <EyeOff className="size-5" /> : <Eye className="size-5" />}
                  </button>
                </div>
              </div>

              {error && <p className="text-sm font-medium text-[#B74A2E]">{error}</p>}

              <button type="submit" disabled={isLoading}
                className={cn("cc-interactive flex h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#FF6500] px-5 text-[15px] font-extrabold text-[#090909] shadow-sm hover:bg-[#E95800] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#FF6500]/20 disabled:opacity-60")}> 
                {isLoading ? <><Loader2 className="size-4 animate-spin" /> Entrando…</> : <>Iniciar sesión <ArrowRight className="size-5" /></>}
              </button>
            </form>

            <p className="mt-16 text-center text-xs text-[#AAA7A0]">Versión 1.0</p>
          </div>
        </div>
      </div>
    </div>
  );
}
